<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  FunnelX,
  Plus,
  RefreshCw,
  Search,
} from 'lucide-vue-next';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import DateRangePicker from '~/components/shared/DateRangePicker.vue';
import Spinner from '~/components/Spinner/Spinner.vue';
import DashboardLayout from '~/layouts/DashboardLayout.vue';
import Pagination from '~/components/Pagination';
import {
  CESSION_CATEGORY_COLOR_CLASS,
  CESSION_CATEGORY_LABELS,
  CESSION_OVERTURNING_BUTTON_LABELS,
  CESSION_OVERTURNING_LABELS,
  CESSION_OVERTURNING_TABLE_COLUMNS,
  CESSION_SEARCH_TYPE_OPTIONS,
  CESSION_STATUS_OPTIONS,
  CESSION_TYPE_OPTIONS,
  type CessionStatus,
  getCessionStatusColorClass,
} from '~/constants/capitalMarkets';
import { formatCurrencyForDisplay } from '~/utils/formatters';
import getErrorMessage from '~/utils/getErrorMessage';

import useCessionFilters from '../hooks/useCessionFilters';
import useTableSorting from '../hooks/useTableSorting.ts';
import useCessions from '../queries/useCessions.ts';
import type { Cession } from '../services/capitalMarketsService';

const router = useRouter();
const TABLE_COLUMNS = CESSION_OVERTURNING_TABLE_COLUMNS;

const {
  apiParams,
  dateRange,
  handleReset,
  handleSearch,
  initializeForm,
  searchQueryModel,
  searchQueryProps,
  searchTypeModel,
  searchTypeProps,
  setPage,
  setPageSize,
  statusModel,
  statusProps,
  typeModel,
  typeProps,
} = useCessionFilters({
  onFiltersChange: () => {
  },
});

const {
  data: cessionsData,
  error: cessionsError,
  isError: isCessionsError,
  isLoading: isLoadingCessions,
  refetch: refetchCessions,
} = useCessions(apiParams as any);

const cessions = computed<Cession[]>(() => {
  if (cessionsData.value?.items && cessionsData.value.items.length > 0) {
    return cessionsData.value.items;
  }
  return [];
});

const {
  handleSort,
  sortedCessions,
  sortState,
} = useTableSorting(() => cessions.value);

// Garante o tipo correto
const typedSortedCessions = computed<Cession[]>(() => sortedCessions.value);

const hasNext = computed(() => !!cessionsData.value?.has_next);
const pageSize = computed(() => Number(apiParams.value.page_size) || 10);
const currentPage = computed(() => Number(apiParams.value.page) || 1);
const hasItems = computed(() => cessions.value.length > 0);
const errorMessage = computed(() => getErrorMessage(cessionsError.value));

function formatDate(dateString?: string): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return dateString;
  }
}

function formatMoney(value?: null | number): string {
  return formatCurrencyForDisplay(value ?? 0);
}

function getCategoryLabel(category?: string): string {
  if (!category) return '-';
  return CESSION_CATEGORY_LABELS[category as keyof typeof CESSION_CATEGORY_LABELS] || category;
}

function getStatusLabel(status?: string): string {
  if (!status) return '-';
  const statusOption = CESSION_STATUS_OPTIONS.find((option) => option.value === status);
  return statusOption?.name || status;
}

function handlePageSizeChange(newPageSize: number): void {
  setPageSize(newPageSize);
}

function handleSetPage(value: number): void {
  setPage(value);
}

const handleCreateCession = () => {
  router.push('/cessions/create?category=new_cession');
};

const handleCreateOverturning = () => {
  router.push('/cessions/create?category=overturning');
};

const handleRetry = () => {
  refetchCessions();
};

function handleCessionClick(cession: Cession): void {
  router.push(`/cessions/${cession.cession_id}`);
}

const actionButtons = [
  {
    handler: handleCreateCession,
    label: CESSION_OVERTURNING_BUTTON_LABELS.CREATE_CESSION,
  },
  {
    handler: handleCreateOverturning,
    label: CESSION_OVERTURNING_BUTTON_LABELS.CREATE_OVERTURNING,
  },
] as const;

onMounted(() => {
  document.title = 'Cessão e Tombamento';
  initializeForm();
});
</script>

<template>
  <DashboardLayout title="Cessão e Tombamento" description="Lista de cessões e tombamentos">
    <div class="row row-cards">
      <div class="col-12">
        <div class="card position-relative">
          <div class="card-header d-flex justify-content-end align-items-end gap-2 flex-wrap">
            <button
              v-for="(button, index) in actionButtons"
              :key="index"
              type="button"
              class="btn btn-primary action-button"
              @click="button.handler"
            >
              <Plus class="icon icon-sm me-1" />
              <span class="button-label">{{ button.label }}</span>
            </button>
          </div>

          <!-- Filtros -->
          <div class="card-body border-bottom py-3">
            <div class="d-flex gap-3 flex-wrap align-items-end">
              <div class="mb-3 flex-grow-1" style="min-width: 200px;">
                <label class="form-label" for="search_type">Buscar por:</label>
                <select
                  id="search_type"
                  class="form-select"
                  v-model="searchTypeModel"
                  v-bind="searchTypeProps"
                  aria-label="Campo de seleção de tipo de busca"
                >
                  <option disabled :value="undefined">Selecione...</option>
                  <option
                    v-for="option in CESSION_SEARCH_TYPE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3 flex-grow-1" style="min-width: 200px;">
                <label class="form-label" for="search_query" />
                <input
                  id="search_query"
                  v-model="searchQueryModel"
                  v-bind="searchQueryProps"
                  type="text"
                  class="form-control"
                  placeholder="Digite o valor da busca"
                  aria-label="Campo de busca"
                  :disabled="!searchTypeModel"
                  @keydown.enter="handleSearch"
                />
              </div>
              <div class="mb-3 flex-grow-1" style="min-width: 200px;">
                <label class="form-label" for="type">Tipo</label>
                <select
                  id="type"
                  class="form-select"
                  v-model="typeModel"
                  v-bind="typeProps"
                  aria-label="Campo de seleção de tipo"
                >
                  <option disabled :value="undefined">Selecione...</option>
                  <option
                    v-for="option in CESSION_TYPE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3 flex-grow-1" style="min-width: 200px;">
                <label class="form-label" for="status">Status</label>
                <select
                  id="status"
                  class="form-select"
                  v-model="statusModel"
                  v-bind="statusProps"
                  aria-label="Campo de seleção de status"
                >
                  <option disabled :value="undefined">Selecione...</option>
                  <option
                    v-for="option in CESSION_STATUS_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.name }}
                  </option>
                </select>
              </div>
              <div class="mb-3 flex-grow-1" style="min-width: 200px;">
                <label class="form-label" for="date_range">{{ CESSION_OVERTURNING_LABELS.DATE_CESSION }}</label>
                <DateRangePicker
                  id="date_range"
                  v-model="dateRange"
                  mode="range"
                  placeholder="Selecione o período"
                  @keydown.enter="handleSearch"
                />
              </div>
              <div class="mb-3 d-flex gap-2 button-group">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="handleSearch"
                >
                  <Search class="icon icon-sm me-1" />
                  Buscar
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="handleReset"
                >
                  <FunnelX class="icon icon-sm me-1" />
                  Limpar
                </button>
              </div>
            </div>
          </div>

          <!-- Estado de erro -->
          <div v-if="isCessionsError && !isLoadingCessions" class="card-body error-state">
            <div class="alert alert-danger" role="alert">
              <div class="alert-content">
                <div class="d-flex align-items-center gap-2 mb-3">
                  <AlertCircle class="icon icon-sm" />
                  <strong>Erro ao carregar cessões</strong>
                </div>
                <p class="mb-3">
                  Ocorreu um erro ao carregar as cessões. Por favor, tente novamente.
                </p>
                <div v-if="errorMessage && errorMessage !== 'UNKNOWN'" class="mb-3">
                  <small class="text-muted">Detalhes: {{ errorMessage }}</small>
                </div>
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="handleRetry"
                >
                  <RefreshCw class="icon icon-sm me-1 text-white" />
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>

          <div v-else-if="!isLoadingCessions && !hasItems" class="empty-state">
            <div class="empty-state-content">
              <AlertCircle class="icon icon-lg text-muted" />
              <p class="text-secondary mb-0">
                <strong>Nenhuma cessão encontrada</strong>
              </p>
              <p class="text-muted small mb-0">
                Tente ajustar os filtros de busca para encontrar mais resultados.
              </p>
            </div>
          </div>

          <!-- Tabela de dados -->
          <div v-else-if="!isLoadingCessions && hasItems" class="table-responsive">
            <table class="table table-vcenter">
              <thead>
                <tr>
                  <th
                    v-for="column in TABLE_COLUMNS"
                    :key="column.value"
                    scope="col"
                    :class="{
                      'table-header': true,
                      'table-header-sortable': column.sort,
                      'table-header-sorted': column.sort && sortState.column === column.value,
                    }"
                    @click="column.sort ? handleSort(column.value) : null"
                  >
                    <div class="table-header-content">
                      <span class="table-header-text">{{ column.name }}</span>
                      <div v-if="column.sort" class="table-header-sort">
                        <!-- Desktop: dois ícones separados -->
                        <div class="table-header-sort-desktop">
                          <ChevronUp
                            class="icon sort-icon"
                            @click.stop="handleSort(column.value)"
                            :stroke="sortState.column === column.value
                              && sortState.direction === 'asc'
                              ? 'black'
                              : 'gray'"
                          />
                          <ChevronDown
                            class="icon sort-icon"
                            @click.stop="handleSort(column.value)"
                            :stroke="sortState.column === column.value
                              && sortState.direction === 'desc'
                              ? 'black'
                              : 'gray'"
                          />
                        </div>
                        <!-- Mobile: um único ícone que muda -->
                        <div class="table-header-sort-mobile">
                          <ChevronUp
                            v-if="sortState.column === column.value && sortState.direction === 'asc'"
                            class="icon sort-icon-mobile"
                          />
                          <ChevronDown
                            v-else-if="sortState.column === column.value && sortState.direction === 'desc'"
                            class="icon sort-icon-mobile"
                          />
                          <ChevronUp
                            v-else
                            class="icon sort-icon-mobile sort-icon-inactive"
                          />
                        </div>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                <!-- Dados -->
                <tr
                  v-for="cession in typedSortedCessions"
                  :key="cession.cession_id"
                  class="cursor-pointer"
                  @click="handleCessionClick(cession)"
                >
                  <td>
                    <span class="text-secondary">
                      {{ formatDate(cession.operation_date || cession.cut_off_date) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ cession.cession_id }}</span>
                  </td>
                  <td>
                    <span class="tag">
                      <span
                        class="badge badge-3 text-white tag-status badge-dot"
                        :class="`bg-${
                          CESSION_CATEGORY_COLOR_CLASS[
                            cession.operation_type as keyof typeof CESSION_CATEGORY_COLOR_CLASS
                          ]
                        }`"
                      />
                      {{ getCategoryLabel(cession.operation_type) }}
                    </span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ cession.securitization_name.toUpperCase() || '-' }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ formatMoney(cession.financed_amount) }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ formatMoney(cession.gross_amount) }}</span>
                  </td>
                  <td>
                    <span class="text-secondary">{{ formatMoney(cession.acquisition_amount) }}</span>
                  </td>
                  <td class="text-nowrap">
                    <span
                      class="badge"
                      :class="`bg-${getCessionStatusColorClass(cession.status as CessionStatus)}-lt`"
                    >
                      {{ getStatusLabel(cession.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="isLoadingCessions" class="loading-state">
            <Spinner />
            <p class="text-muted mt-2 mb-0">Carregando cessões...</p>
          </div>

          <div class="pagination-wrapper">
            <Pagination
              v-if="!isCessionsError && hasItems"
              :current-page="currentPage"
              :has-next="hasNext"
              :page-size="pageSize"
              @page-change="handleSetPage"
              @page-size-change="handlePageSizeChange"
            />
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
// Responsive breakpoints
$mobile: 576px;
$tablet: 768px;
$desktop: 992px;

.table-responsive {
  min-height: 50vh;
  overflow-x: auto;
  overflow-y: auto;
}

.card {
  .card-header {
    @media (max-width: $tablet) {
      justify-content: center !important;
      padding: 1rem;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    @media (max-width: $mobile) {
      flex-direction: row;
      justify-content: stretch !important;
    }
  }
}

.action-button {
  @media (max-width: $tablet) {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 0;

    .button-label {
      display: inline;
    }
  }

  @media (max-width: $mobile) {
    flex: 1 1 calc(50% - 0.25rem);
    min-width: 0;
    width: auto;
  }

  @media (min-width: #{$tablet + 1px}) and (max-width: $desktop) {
    flex: 0 1 auto;
  }
}

.button-label {
  @media (max-width: $mobile) {
    display: none;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.break-anywhere {
  word-break: break-word;
}

.table {
  tbody {
    tr {
      background-color: white !important;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: #f8f9fa !important;
      }

      &.cursor-pointer {
        cursor: pointer;
      }
    }
  }

  th,
  td {
    white-space: nowrap;

    @media (max-width: $tablet) {
      white-space: normal;
      font-size: 0.875rem;
    }
  }

  th {
    &.table-header {
      padding: 0.75rem 0.5rem;
      vertical-align: middle;

      @media (max-width: $tablet) {
        padding: 0.5rem 0.25rem;
        min-width: 80px;
      }

      @media (max-width: $mobile) {
        padding: 0.4rem 0.2rem;
        min-width: 70px;
      }

      &.table-header-sortable {
        cursor: pointer;
        user-select: none;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: #f8f9fa;
        }

        @media (max-width: $tablet) {
          &:active {
            background-color: #e9ecef;
          }
        }
      }

      &.table-header-sorted {
        background-color: #f0f4f8;
      }
    }

    .table-header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      width: 100%;

      @media (max-width: $tablet) {
        flex-direction: row;
        gap: 0.5rem;
        align-items: center;
      }

      @media (max-width: $mobile) {
        gap: 0.3rem;
      }
    }

    .table-header-text {
      word-break: break-word;
      line-height: 1.2;
      font-size: 0.875rem;
      font-weight: 600;
      flex: 1;

      @media (max-width: $tablet) {
        font-size: 0.75rem;
        line-height: 1.1;
      }

      @media (max-width: $mobile) {
        font-size: 0.7rem;
      }
    }

    .table-header-sort {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .table-header-sort-desktop {
        display: flex;
        flex-direction: column;
        cursor: pointer;
        border: 1px solid #dee2e6;
        border-radius: 0.25rem;
        padding: 0.125rem;
        margin-left: 0.5rem;

        @media (max-width: $tablet) {
          display: none;
        }

        .sort-icon {
          width: 0.75rem;
          height: 0.75rem;
        }
      }

      .table-header-sort-mobile {
        display: none;

        @media (max-width: $tablet) {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 0.25rem;
        }

        .sort-icon-mobile {
          width: 1rem;
          height: 1rem;
          stroke: #6c757d;

          @media (max-width: $mobile) {
            width: 0.875rem;
            height: 0.875rem;
          }

          &.sort-icon-inactive {
            opacity: 0.4;
          }
        }
      }
    }
  }
}

// Responsive adjustments for filter form
.card-body {
  .button-group {
    flex-shrink: 0;
    white-space: nowrap;

    @media (max-width: $mobile) {
      width: 100%;
      justify-content: stretch;

      button {
        flex: 1;
      }
    }
  }

  &.error-state {
    padding: 2rem;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;

    .alert {
      width: 100%;
      max-width: 600px;
      margin: 0 auto;
      border: 1px solid #f5c6cb;
      border-radius: 0.5rem;
      padding: 1.5rem;

      .alert-content {
        .icon {
          flex-shrink: 0;
          color: #dc3545;
        }

        strong {
          color: #721c24;
        }

        p {
          color: #721c24;
          margin-bottom: 1rem;
        }

        small {
          display: block;
          color: #6c757d;
          font-size: 0.875rem;
        }

        .btn {
          margin-top: 0.5rem;

          .text-white {
            color: white !important;
            stroke: white;
          }
        }
      }
    }
  }
}

.icon-lg {
  width: 3rem;
  height: 3rem;
}

.empty-state {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;

  .empty-state-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
  }
}

.loading-state {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
}

@media (max-width: 768px) {
  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table {
    min-width: 800px;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
    overflow-x: auto;
    padding: 0 1rem;
  }

  .pagination-wrapper :deep(.d-flex) {
    flex-wrap: wrap;
    justify-content: center !important;
    padding-right: 0 !important;
    width: 100%;
  }
}

@media (min-width: 769px) {
  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
